/** Registering a customer's structure from a file - **ADR-0091**, the browser's half.
 *
 * Three calls, all an Admin's: the template (a link, not a fetch), a dry run and the apply. The dry
 * run and the apply take **the same `File` object**, for the reason `useInventoryApply` does: a
 * second pick would let the file change between looking and applying, and the thing a person
 * approved would not be the thing that ran.
 *
 * `fetch` directly, because multipart is one of the few things the generated client does not model.
 *
 * **A repeat is safe, and the screen may say so.** The apply is create-only: a file whose every row
 * already exists as stated writes nothing and answers `already_registered`. So when a response is
 * lost (`isAmbiguous`), the remedy is to send the same file again, and this module is where that
 * is true rather than hoped.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiUrl } from "./client";
import { Refused } from "./refusal";
import type { components } from "./schema";

/** Derived from the generated schema, so a field the server adds or renames fails `tsc` here. */
export type StructureDryRun = components["schemas"]["StructureDryRunOut"];
export type StructureApplied = components["schemas"]["StructureAppliedOut"];
export type StructureRow = components["schemas"]["StructureRowOut"];
export type StructureCounts = components["schemas"]["StructureCountsOut"];
export type StructureRefusal = components["schemas"]["StructureRefusalOut"];

/** Where the blank file is. A link the browser follows: the response is a file. */
export function structureTemplateUrl(format: "xlsx" | "csv"): string {
  return apiUrl(`/api/v1/org/structure/template?format=${format}`);
}

/** Why a file was refused. `rows` is present on `file_refused`, and then it is the whole list:
 * every row's verdict, so one correction pass fixes everything the file got wrong. */
export class StructureRefused extends Refused {
  readonly rows: readonly StructureRow[];

  constructor(status: number, code: string, message: string, rows: readonly StructureRow[] = []) {
    super(status, code, message);
    this.name = "StructureRefused";
    this.rows = rows;
  }

  /** The file was read and some rows cannot be registered. Not a fault in the request. */
  get isFileRefused(): boolean {
    return this.code === "file_refused";
  }
}

async function refusalOf(response: Response): Promise<StructureRefused> {
  // Parsed defensively: a 413 from a proxy in front of the API arrives as HTML, and a parse error
  // would otherwise replace "that file is too large" with a message about a JSON token.
  try {
    const payload = (await response.json()) as {
      detail?: { error?: string; message?: string; rows?: StructureRow[] } | string;
    };
    const detail = payload.detail;
    if (typeof detail === "string") return new StructureRefused(response.status, "", detail);
    return new StructureRefused(
      response.status,
      detail?.error ?? "",
      detail?.message || `EmitIQ refused this file (${response.status}).`,
      Array.isArray(detail?.rows) ? detail.rows : [],
    );
  } catch {
    return new StructureRefused(
      response.status,
      "",
      `EmitIQ refused this file (${response.status}).`,
    );
  }
}

async function send<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append("file", file);
  const response = await apiFetch(`/api/v1/org/structure/${path}`, { method: "POST", body });
  if (!response.ok) throw await refusalOf(response);
  return (await response.json()) as T;
}

/** What the file **would** do. Writes nothing. */
export function useStructureDryRun() {
  return useMutation({
    mutationFn: (file: File): Promise<StructureDryRun> => send<StructureDryRun>("dry-run", file),
  });
}

/** Register the file, or refuse all of it.
 *
 * Every list that names a site, a meter or a stream changes, so the whole cache is invalidated
 * rather than a key: naming them would be a list to keep in step with every screen added later.
 */
export function useStructureApply() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File): Promise<StructureApplied> => send<StructureApplied>("apply", file),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
